import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ToastContainer, Slide } from 'react-toastify';
import {
  ModalResponsive,
  Loading,
  LoadingCircle,
  Pagination,
  UsersCard,
  Tooltip,
} from '../../../components';
import Edit from '../../../assets/buttons/edit.svg';
import Analisys from '../../../assets/buttons/analisys.svg';
import Deactivate from '../../../assets/buttons/deactivate.svg';
import Reactivate from '../../../assets/buttons/reactivate.svg';
import GroupAdd from '../../../assets/buttons/group-add.svg';
import { useAccess, useClient } from '../../../hooks';
import {
  searchArrayOfObjects,
  sendNotification,
  formatDocumentCNPJ,
} from '../../../utils/functions';
import makeAnimated from 'react-select/animated';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  WithContext as ReactTags,
  type Tag as ReactTag,
} from 'react-tag-input';

import { FaRegCircleQuestion } from 'react-icons/fa6';
import { MdOutlineEdit } from 'react-icons/md';
import { Container, Content } from '../../../styles/lib/grid';
import { TabsContainer } from '../../../styles/lib/tabs';
import * as S from './UsersStyles';
import { RegistrationClientForm } from './Components/RegistrationClientForm/RegistrationClientForm';
import ValidationUserDialog from './Components/ValidationUser/ValidationDialog';
import { Select, Option } from '../../../styles/lib/dynamic-filter';
import { IProfileGroup } from '../../../interfaces/permissions';
import { AxiosResponse } from 'axios';
import { usePagination } from '../../../hooks/usePagination';

interface UsersProps {
  setTotalEmAnalise: Dispatch<SetStateAction<string | null>>;
}

interface CnpjTagsFieldProps {
  tags: any[];
  onAdd: (tag: ReactTag) => void;
  onDelete: (index: number) => void;
  onDrag: (tag: ReactTag, currPos: number, newPos: number) => void;
}

const CnpjTagsField: React.FC<CnpjTagsFieldProps> = ({
  tags,
  onAdd,
  onDelete,
  onDrag,
}) => {
  const cleanupRef = useRef<(() => void) | null>(null);

  const containerCallback = useCallback((node: HTMLDivElement | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!node) return;

    const timerId = setTimeout(() => {
      const input = node.querySelector('input') as HTMLInputElement | null;
      if (!input) return;

      const nativeValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value',
      )?.set;

      const setInputValue = (value: string) => {
        nativeValueSetter?.call(input, value);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      };

      const onKeyDown = (e: KeyboardEvent) => {
        if (e.ctrlKey || e.metaKey) return;

        if (e.key === 'Backspace') {
          const clean = input.value.replace(/[./-]/g, '');
          if (clean.length === 0) return;
          e.preventDefault();
          const newClean = clean.slice(0, -1);
          setInputValue(newClean ? formatDocumentCNPJ(newClean) : '');
          return;
        }

        if (e.key.length > 1 || e.key === ',') return;
        e.preventDefault();
        if (!/^[A-Za-z0-9]$/.test(e.key)) return;

        const clean = input.value.replace(/[./-]/g, '');
        if (clean.length >= 14) return;

        setInputValue(formatDocumentCNPJ(clean + e.key.toUpperCase()));
      };

      const onPaste = (e: ClipboardEvent) => {
        e.preventDefault();
        const pasted = (e.clipboardData?.getData('text') ?? '')
          .replace(/[^A-Za-z0-9]/g, '')
          .toUpperCase()
          .slice(0, 14);
        setInputValue(formatDocumentCNPJ(pasted));
      };

      input.addEventListener('keydown', onKeyDown);
      input.addEventListener('paste', onPaste);
      cleanupRef.current = () => {
        input.removeEventListener('keydown', onKeyDown);
        input.removeEventListener('paste', onPaste);
      };
    }, 0);

    cleanupRef.current = () => clearTimeout(timerId);
  }, []);

  return (
    <div ref={containerCallback}>
      <ReactTags
        classNames={{ tags: 'ReactTags__tags secondary' }}
        tags={tags}
        handleAddition={onAdd}
        handleDelete={onDelete}
        handleDrag={onDrag}
        delimiters={[188, 13]}
        placeholder="Digite aqui os CNPJs..."
      />
    </div>
  );
};

const toastConfig = {
  autoClose: 3000,
  hideProgressBar: true,
  type: 'success',
};

const errorToastConfig = {
  autoClose: 9000,
  hideProgressBar: true,
  type: 'error',
};

export const NewUsers: React.FC<UsersProps> = ({ setTotalEmAnalise }) => {
  const {
    returnAllUsers,
    changeUserStatus,
    editUserInfo,
    analyzeUser,
    isLoading,
    isLoadingAPICall,
    representantes,
    metadata,
  } = useAccess();

  const [
    { profileGroups, responsibleChannel },
    fetchProfilesGroups,
    ,
    ,
    ,
    ,
    fetchResponsibleChannel,
  ] = usePermissions();

  const [apiError, setApiError] = useState<string | null>(null);

  const [area, setArea] = useState<any>([]);
  const [profile, setProfile] = useState('');
  const [cnpjsRaiz, setCnpjsRaiz] = useState<any>([]);
  const [cnpjTagInput, setCnpjTagInput] = useState('');
  const [{ reject }, consultingCnpj] = useClient();

  const [openEditModal, setOpenEditModal] = useState(false);
  const [openAnalysisModal, setOpenAnalysisModal] = useState(false);
  const [openAnalysisModalRevendedor, setOpenAnalysisModalRevendedor] =
    useState(false);
  const [openReactivateModal, setOpenReactivateModal] = useState(false);
  const [openDeactivateModal, setOpenDeactivateModal] = useState(false);
  const [openRegisterModal, setOpenRegisterModal] = useState(false);

  const [filters, setFilters] = useState<{
    type: string;
    term: string;
    tipoPerfil: string;
  }>({
    type: '',
    term: '',
    tipoPerfil: '',
  });

  const [editDealer, setEditDealder] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [isApprove, setIsApprove] = useState(true);
  const [reproveReason, setReproveReason] = useState('');

  useEffect(() => {
    fetchProfilesGroups({ active: true });
    fetchResponsibleChannel();
    handleAllUsers();
    setApiError(null);
  }, []);

  const animatedSelectComponents = makeAnimated();

  useEffect(() => {
    if (!openEditModal) {
      setCnpjsRaiz([]);
    } else {
      patchCnpjsRaiz(user);
    }
  }, [openEditModal]);

  const profileTypes = useMemo(() => {
    return profileGroups.map((e: any) => {
      return e;
    });
  }, [profileGroups]);

  useEffect(() => {
    setTotalEmAnalise(metadata.totalEmAnalise);
  }, [setTotalEmAnalise, metadata]);

  const handleAreaChoosen = (area: any, user: any) => {
    setUser({ ...user, canaisResponsaveis: area });
    setArea(area);
  };

  const { currentPage, pageSize, handlePageChange, resetPagination } =
    usePagination({
      totalItems: representantes.pagination?.totalItems || 0,
      onPageChange: (currentPage, pageSize) => {
        returnAllUsers({
          ...filters,
          currentPage,
          pageSize,
          isNew: true,
        });
      },
    });

  const handleAllUsers = () => {
    resetPagination();
    returnAllUsers({
      ...filters,
      pageSize,
      currentPage: 1,
      isNew: true,
    });
  };

  const searchUsers = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleAllUsers();
  };

  const handleModalClose = async () => {
    setOpenRegisterModal(false);
    handleAllUsers();
  };

  const handleUserAction = async (
    actionFn: () => Promise<AxiosResponse | undefined>,
    successMessage: string,
    errorMessage?: string,
  ) => {
    try {
      await actionFn();
      sendNotification(successMessage, toastConfig);
      setOpenAnalysisModal(false);
      setOpenEditModal(false);
      setReproveReason('');
      setApiError(null);
    } catch (e: any) {
      const errorToDisplay =
        errorMessage || e.response?.data?.errors || e.message;
      setApiError(errorToDisplay);
    }
  };

  const patchCnpjsRaiz = (user: any) => {
    if (!user || !user.cnpj) {
      return;
    }

    const existingCnpjs = new Set(
      cnpjsRaiz.map((cnpj: { id: string; text: string }) => cnpj.id),
    );

    const uniqueFormattedCnpjs = user.cnpj
      .filter((cnpj: any) => !existingCnpjs.has(cnpj))
      .map((cnpj: any) => ({
        id: cnpj,
        text: formatCnpj(cnpj),
      }));

    setCnpjsRaiz((prevCnpjsRaiz: any) => {
      const updatedCnpjs = [...prevCnpjsRaiz, ...uniqueFormattedCnpjs];
      const uniqueCnpjs = Array.from(new Set(updatedCnpjs.map(c => c.id))).map(
        id => updatedCnpjs.find(c => c.id === id),
      );

      return uniqueCnpjs;
    });
  };

  const formatCnpj = (cnpj: string) => {
    if (typeof cnpj !== 'string') {
      return cnpj;
    }
    return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(
      5,
      8,
    )}/${cnpj.slice(8, 12)}-${cnpj.slice(12, 14)}`;
  };

  const updateUser = async () => {
    const response: any = await editUserInfo({
      ...user,
      areasAtuacao: area,
      cnpjsRaiz: cnpjsRaiz.map((i: { id: string; text: string }) =>
        i.id.replace(/[./-]/g, '').toUpperCase(),
      ),
    });
    if (response?.status >= 200 && response?.status < 300) {
      setOpenEditModal(false);
      sendNotification(
        `As alterações no usuário ${
          user?.nome ?? user?.nomeContato ?? user?.razaoSocial
        } foram realizadas com sucesso.`,
        toastConfig,
      );

      handleAllUsers();
    }
  };

  const approveUser = async () => {
    try {
      const response: any = await analyzeUser(
        user?.id,
        true,
        reproveReason,
        false,
      );
      if (response?.status >= 200 && response?.status < 300) {
        sendNotification(
          `O usuário ${
            user?.razaoSocial ?? user?.nome ?? user?.nomeContato ?? ''
          } foi aprovado.`,
          toastConfig,
        );
        setOpenAnalysisModal(false);
        setReproveReason('');
        setApiError(null);
        handleAllUsers();
      } else {
        const errorToDisplay =
          response?.data?.errors ||
          response?.statusText ||
          'Erro desconhecido na API';
        setApiError(errorToDisplay);
        sendNotification(
          `Falha na aprovação: ${errorToDisplay}`,
          errorToastConfig,
        );
      }
    } catch (e: any) {
      const errorToDisplay = e.response?.data?.errors || e.message;
      setApiError(errorToDisplay);
      sendNotification(`Erro ao aprovar: ${errorToDisplay}`, errorToastConfig);
    }
  };

  const reproveUser = async () => {
    try {
      const response: any = await analyzeUser(
        user?.id,
        false,
        reproveReason,
        false,
      );
      if (response?.status >= 200 && response?.status < 300) {
        sendNotification(
          `O usuário ${
            user?.razaoSocial ?? user?.nome ?? user?.nomeContato ?? ''
          } foi reprovado.`,
          toastConfig,
        );
        setOpenAnalysisModal(false);
        setReproveReason('');
        setApiError(null);
        handleAllUsers();
      } else {
        const errorToDisplay =
          response?.data?.errors ||
          response?.statusText ||
          'Erro desconhecido na API';
        setApiError(errorToDisplay);
        sendNotification(
          `Falha na reprovação: ${errorToDisplay}`,
          errorToastConfig,
        );
      }
    } catch (e: any) {
      const errorToDisplay = e.response?.data?.errors || e.message;
      setApiError(errorToDisplay);
      sendNotification(`Erro ao reprovar: ${errorToDisplay}`, errorToastConfig);
    }
  };

  const activateUser = async () => {
    await changeUserStatus(user?.id, true);
    sendNotification(
      `O usuário ${user?.razaoSocial} foi reativado.`,
      toastConfig,
    );
    setOpenReactivateModal(false);

    handleAllUsers();
  };

  const deactivateUser = async () => {
    await changeUserStatus(user?.id, false);
    sendNotification(
      `O usuário ${user?.razaoSocial} foi desativado.`,
      toastConfig,
    );
    setOpenDeactivateModal(false);
    handleAllUsers();
  };

  const renderGrupoEconomico = (grupoEconomico: any) => {
    if (
      !grupoEconomico ||
      !Array?.isArray(grupoEconomico) ||
      grupoEconomico?.length === 0
    ) {
      return '-';
    }

    const primary =
      typeof grupoEconomico?.[0] === 'string'
        ? grupoEconomico?.[0]
        : 'Principal';
    const others = Array.isArray(grupoEconomico)
      ? grupoEconomico?.slice(1)
      : [];

    return (
      <S.ListInline>
        <ul className="no-flex">
          <li>
            <span>Principal</span> {primary}
          </li>
          {others?.length > 0 && (
            <li className="same">
              <span>Outros</span> {others.join(', ')}
            </li>
          )}
        </ul>
      </S.ListInline>
    );
  };

  const treatmentUserAttributes = (user: any) => {
    setUser(user);
    setProfile(user.perfil?.nome);
    setArea(null);
    if (user?.canaisResponsaveis?.length > 0) {
      const canaisResponsaveis: any = user?.canaisResponsaveis.map((r: any) => {
        const matchingGroup: any = responsibleChannel.find(
          (area: any) => area.label === r,
        );
        const value = matchingGroup ? matchingGroup.value : null;
        return {
          value,
          label: r,
        };
      });
      setArea(canaisResponsaveis);
    } else {
      setArea([]);
    }
  };

  const formatCNPJInput = (value: string): string => {
    const raw = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    const alphaPart = raw.slice(0, 12);
    const numericPart = raw.slice(12).replace(/\D/g, '').slice(0, 2);
    const clean = alphaPart + numericPart;
    let result = '';
    for (let i = 0; i < clean.length; i++) {
      if (i === 2) result += '.';
      if (i === 5) result += '.';
      if (i === 8) result += '/';
      if (i === 12) result += '-';
      result += clean[i];
    }
    return result;
  };

  const handleAdditionCnpjs = async (tag: ReactTag) => {
    const cnpjText = tag.text.replace(/[./-]/g, '').toUpperCase();

    const formattedCnpj = formatDocumentCNPJ(cnpjText);

    const newCnpj = {
      id: tag.id,
      text: formattedCnpj,
    };

    const responseConsulted = await consultingCnpj(cnpjText);
    const grupoEconomico = responseConsulted?.cliente?.grupoEconomico || '';

    if (grupoEconomico && !user?.grupoEconomico.includes(grupoEconomico)) {
      setCnpjsRaiz([...cnpjsRaiz, newCnpj]);
      setCnpjTagInput('');
      setUser({
        ...user,
        cnpj: [...(user.cnpj ?? []), tag.id],
        grupoEconomico: [...user.grupoEconomico, grupoEconomico],
      });
    }
  };

  const handleDeleteCnpjs = (index: number) => {
    if (!Array.isArray(user?.cnpj)) return;
    const newCnpjs = cnpjsRaiz.filter((_: string, i: number) => i !== index);
    setCnpjsRaiz(newCnpjs);

    const newUserCnpjs = user.cnpj.filter(
      (_: string, i: number) => i !== index,
    );

    const newGrupoEconomico = user.grupoEconomico.filter(
      (_: string, i: number) => i !== index,
    );

    setUser({
      ...user,
      cnpj: newUserCnpjs,
      grupoEconomico: newGrupoEconomico,
    });
  };

  const handleCnpjsDrag = (tag: any, currPos: number, newPos: number) => {
    const newCnpjs = [...cnpjsRaiz];
    const [movedCnpj] = newCnpjs.splice(currPos, 1);
    newCnpjs.splice(newPos, 0, movedCnpj);
    setCnpjsRaiz(newCnpjs);

    const newUserCnpjs = [...user.cnpj];
    const [movedUserCnpj] = newUserCnpjs.splice(currPos, 1);
    newUserCnpjs.splice(newPos, 0, movedUserCnpj);

    const newGrupoEconomico = [...user.grupoEconomico];
    const [movedGrupoEconomico] = newGrupoEconomico.splice(currPos, 1);
    newGrupoEconomico.splice(newPos, 0, movedGrupoEconomico);

    setUser({
      ...user,
      cnpj: newUserCnpjs,
      grupoEconomico: newGrupoEconomico,
    });
  };

  const isRevendedor = (perfil: string) => {
    return perfil === 'revendedor-allied';
  };

  const renderRepresentantes = (representantes: any) => {
    const novosUsuarios = representantes?.filter(
      (representante: any) => !representante?.analisado,
    );
    const cadastrados = representantes?.filter(
      (representante: any) => representante?.analisado,
    );

    return (
      <>
        <h2>Novos Usuários</h2>
        <hr style={{ margin: '10px 0 20px 0' }} />
        {novosUsuarios?.map((elm: any) => (
          <UsersCard
            key={elm?.id}
            user={elm}
            isAccordion={!isRevendedor(elm?.perfil?.nome)}
            action={() => {
              setEditDealder(false);
              setIsApprove(true);
              setApiError(null);
              treatmentUserAttributes(elm);
              setOpenAnalysisModalRevendedor(true);
            }}
          >
            {!isRevendedor(elm?.perfil?.nome) && (
              <S.CardButtons>
                {!elm.analisado && (
                  <S.IconButton
                    className="analisys"
                    onClick={() => {
                      setOpenAnalysisModal(true);
                      setIsApprove(true);
                      setApiError(null);
                      treatmentUserAttributes(elm);
                    }}
                  >
                    <img src={Analisys} width={16} />
                    Analisar
                  </S.IconButton>
                )}
                {elm.ativo && elm.analisado && (
                  <S.IconButton
                    className="deactivate"
                    onClick={() => {
                      setOpenDeactivateModal(true);
                      setUser(elm);
                    }}
                  >
                    <img src={Deactivate} width={16} />
                    Desativar
                  </S.IconButton>
                )}
                {!elm.ativo && elm.analisado && (
                  <S.IconButton
                    className="reactivate"
                    onClick={() => {
                      setOpenReactivateModal(true);
                      setUser(elm);
                    }}
                  >
                    <img src={Reactivate} width={16} />
                    Reativar
                  </S.IconButton>
                )}
              </S.CardButtons>
            )}
          </UsersCard>
        ))}
      </>
    );
  };

  return (
    <Container>
      <Content>
        <S.SearchWrapper>
          <S.SearchCell>
            <S.Search onSubmit={searchUsers}>
              <S.InputContainer>
                <S.Select
                  value={filters.type}
                  className="floating"
                  onChange={(e: any) => {
                    setFilters(prev => ({
                      ...prev,
                      type: e.target.value,
                      term: '',
                    }));
                  }}
                >
                  <S.Option value="">Selecione</S.Option>
                  <S.Option value="cnpj">CNPJ</S.Option>
                  <S.Option value="razao">Nome / Razão Social</S.Option>
                  <S.Option value="email">E-mail</S.Option>
                </S.Select>
                <S.Input
                  maxLength={35}
                  value={filters.term}
                  onChange={(e: any) =>
                    setFilters(prev => ({
                      ...prev,
                      term:
                        prev.type === 'cnpj'
                          ? formatDocumentCNPJ(e.target.value)
                          : e.target.value,
                    }))
                  }
                  placeholder={'Faça sua busca...'}
                />
              </S.InputContainer>

              <Select
                className="highlighted"
                value={filters.tipoPerfil}
                onChange={(e: any) =>
                  setFilters(prev => ({ ...prev, tipoPerfil: e.target.value }))
                }
              >
                <Option value="">Todos Perfis</Option>
                {profileGroups
                  .filter((group: IProfileGroup) => group.ativo)
                  .map((group: IProfileGroup, index: number) => (
                    <Option key={index} value={group.nome}>
                      {group.nome}
                    </Option>
                  ))}
              </Select>

              <S.ButtonsContainer>
                <S.Button type="submit">Buscar</S.Button>
              </S.ButtonsContainer>
            </S.Search>

            <S.CardText className="normal">
              Confira os dados, altere ou edite perfis de usuários na lista
              abaixo.
            </S.CardText>
          </S.SearchCell>

          <S.CardInfo>
            <S.CardCell className="left">
              <S.CardText className="active">Ativos</S.CardText>
              <p>
                <S.CardText className="bolder">
                  {metadata.totalAtivos ?? '-'}/
                </S.CardText>
                <S.CardText className="smallest">
                  {metadata.total ?? '-'}
                </S.CardText>{' '}
                <S.CardText className="info">usuários</S.CardText>
              </p>
            </S.CardCell>
            <S.CardCell>
              <S.CardText className="inactive">Inativos</S.CardText>
              <p>
                <S.CardText className="bolder">
                  {metadata.totalInativos ?? '-'}/
                </S.CardText>
                <S.CardText className="smallest">
                  {metadata.total ?? '-'}
                </S.CardText>{' '}
                <S.CardText className="info">usuários</S.CardText>
              </p>
            </S.CardCell>
          </S.CardInfo>
          <S.ButtonsContainer className="register">
            <S.IconButton
              className="primary medium"
              onClick={() => {
                setOpenRegisterModal(true);
              }}
            >
              <img src={GroupAdd} width={16} />
              Novo Revendedor
            </S.IconButton>
          </S.ButtonsContainer>
        </S.SearchWrapper>

        <TabsContainer>
          {isLoading ? (
            <Loading />
          ) : (
            renderRepresentantes(representantes.representantes)
          )}
        </TabsContainer>

        {!isLoading && representantes.representantes?.length === 0 && (
          <S.Message>Nenhum resultado encontrado.</S.Message>
        )}

        {!isLoading && representantes.representantes?.length !== 0 && (
          <Pagination
            currentPage={currentPage}
            paginationSize={pageSize}
            onPageChange={handlePageChange}
            totalItems={representantes.pagination?.totalItems}
          />
        )}
      </Content>

      <>
        <ModalResponsive
          title={'Edição de usuário'}
          isOpenModal={openEditModal}
          handleOpenModal={setOpenEditModal}
          isSmall={false}
        >
          <S.Featured
            className="background flexing"
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <div style={{ flex: 1 }}>
              {[
                {
                  label: 'Nome / Razão Social',
                  value: user?.nome ?? user?.nomeContato ?? user?.razaoSocial,
                },
                { label: 'E-mail', value: (user?.email ?? '-').toLowerCase() },
                { label: 'Perfil do usuário', value: user?.perfil?.nome },
              ].map((item, index) => (
                <React.Fragment key={index}>
                  {item.value !== undefined && (
                    <>
                      <S.Text className="bold top-margin">{item.label}</S.Text>
                      <S.Text>{item.value ?? '-'}</S.Text>
                    </>
                  )}
                </React.Fragment>
              ))}
            </div>

            <div style={{ flex: 1 }}>
              <S.Text className="bold top-margin">
                Grupo(s) econômico(s)
                <Tooltip content="O grupo econômico principal é determinado a partir do primeiro CNPJ raiz informado.">
                  <FaRegCircleQuestion
                    style={{
                      width: '1.1em',
                      height: '1.1em',
                      marginLeft: '5px',
                      color: 'gray',
                    }}
                  />
                </Tooltip>
              </S.Text>
              {renderGrupoEconomico(user?.grupoEconomico)}{' '}
            </div>
          </S.Featured>

          <S.Featured>
            {user?.perfil?.nome !== 'cliente-final' && (
              <>
                <S.Text className="bold">Perfil do usuário</S.Text>
                <S.Select
                  value={profile}
                  onChange={(e: any) => {
                    setProfile(e.target.value);
                    setUser({
                      ...user,
                      perfil: searchArrayOfObjects(
                        profileTypes,
                        'nome',
                        e.target.value,
                      ),
                    });
                  }}
                >
                  {profileTypes.map((e: any) => {
                    return (
                      <S.Option key={e.id} value={e.nome}>
                        {e.nome}
                      </S.Option>
                    );
                  })}
                </S.Select>
              </>
            )}
            <S.FormControl>
              <S.Text className="bold top-margin">CNPJs Raiz</S.Text>
              <ReactTags
                classNames={{ tags: 'ReactTags__tags secondary' }}
                tags={cnpjsRaiz}
                handleAddition={handleAdditionCnpjs}
                handleDelete={handleDeleteCnpjs}
                handleDrag={handleCnpjsDrag}
                delimiters={[188, 13]}
                placeholder="Digite aqui os CNPJs..."
                inputValue={cnpjTagInput}
                handleInputChange={value =>
                  setCnpjTagInput(formatCNPJInput(value))
                }
              />
              {reject && <S.Text className="small-danger">{reject}</S.Text>}
            </S.FormControl>
            <S.FormControl>
              <S.Text className="bold">Canais Responsáveis</S.Text>
              <S.CustomMultiSelect
                classNamePrefix={'Select'}
                className="secondary"
                components={animatedSelectComponents}
                isMulti
                options={responsibleChannel}
                value={area}
                onChange={(opts: any) => handleAreaChoosen(opts, user)}
                placeholder="Selecione"
                isClearable
              />
            </S.FormControl>
          </S.Featured>
          <S.Buttons>
            <S.IconButton
              className="text"
              onClick={() => setOpenEditModal(false)}
            >
              Cancelar
            </S.IconButton>
            <S.IconButton
              className="update"
              onClick={updateUser}
              disabled={isLoadingAPICall}
            >
              {isLoadingAPICall ? <LoadingCircle /> : 'Editar'}
            </S.IconButton>
          </S.Buttons>
        </ModalResponsive>

        <ValidationUserDialog
          isOpen={openAnalysisModalRevendedor}
          user={user}
          onClose={() => {
            setOpenAnalysisModalRevendedor(false);
            handleAllUsers();
          }}
          allowEdit={editDealer}
        />
        <ModalResponsive
          title={'Análise de novo acesso'}
          isOpenModal={openAnalysisModal}
          handleOpenModal={setOpenAnalysisModal}
        >
          <S.Text>
            Você está analisando a solicitação de acesso do usuário{' '}
            <S.Text className="bold inline">
              {user?.nome ?? user?.nomeContato ?? user?.razaoSocial}
            </S.Text>
            . Essa ação concederá ou rejeitará o acesso ao sistema. Você
            confirma?
          </S.Text>
          <S.RadioButtonWrapper>
            <label
              className="form-control"
              onClick={() => {
                setIsApprove(true);
              }}
            >
              <input type="radio" name="radio" checked={isApprove === true} />
              <span className={isApprove ? 'bolder' : ''}>
                Sim, liberar acesso ao usuário.
              </span>
            </label>

            <label
              className="form-control"
              onClick={() => {
                setIsApprove(false);
              }}
            >
              <input type="radio" name="radio" checked={isApprove === false} />
              <span className={!isApprove ? 'bolder' : ''}>
                Não, negar acesso ao usuário.
              </span>
            </label>
          </S.RadioButtonWrapper>
          {!isApprove && (
            <S.InputReason
              value={reproveReason}
              onChange={(e: any) => setReproveReason(e.target.value)}
              placeholder="Informe ao usuário a razão da reprovação da sua solicitação de acesso."
            ></S.InputReason>
          )}
          <S.Buttons className="top-margin">
            <S.IconButton
              className="text"
              onClick={() => setOpenAnalysisModal(false)}
            >
              Cancelar
            </S.IconButton>
            {isApprove && (
              <S.IconButton
                className="success"
                onClick={approveUser}
                disabled={isLoadingAPICall}
              >
                {isLoadingAPICall ? <LoadingCircle /> : 'Aprovar'}
              </S.IconButton>
            )}
            {!isApprove && (
              <S.IconButton
                className={!reproveReason ? 'disabled danger' : 'danger'}
                onClick={reproveUser}
                disabled={isLoadingAPICall || !reproveReason}
              >
                {isLoadingAPICall ? <LoadingCircle /> : 'Reprovar'}
              </S.IconButton>
            )}
          </S.Buttons>
        </ModalResponsive>

        <ModalResponsive
          title={'Desativação de usuário'}
          isOpenModal={openDeactivateModal}
          handleOpenModal={setOpenDeactivateModal}
        >
          <S.Text>
            Você está desativando o usuário{' '}
            <S.Text className="bold inline">
              {user?.nome ?? user?.nomeContato ?? user?.razaoSocial}
            </S.Text>
            . Essa ação bloqueará o acesso ao sistema. Deseja continuar?
          </S.Text>
          <S.Buttons>
            <S.IconButton
              className="text"
              onClick={() => setOpenDeactivateModal(false)}
            >
              Cancelar
            </S.IconButton>
            <S.IconButton
              className="danger"
              onClick={deactivateUser}
              disabled={isLoadingAPICall}
            >
              {isLoadingAPICall ? <LoadingCircle /> : 'Desativar'}
            </S.IconButton>
          </S.Buttons>
        </ModalResponsive>
        <ModalResponsive
          title={'Reativação de usuário'}
          isOpenModal={openReactivateModal}
          handleOpenModal={setOpenReactivateModal}
        >
          <S.Text>
            Você está reativando o usuário{' '}
            <S.Text className="bold inline">
              {user?.nome ?? user?.nomeContato ?? user?.razaoSocial}
            </S.Text>
            . Essa ação concederá o acesso ao sistema. Deseja continuar?
          </S.Text>
          <S.Buttons>
            <S.IconButton
              className="text"
              onClick={() => setOpenReactivateModal(false)}
            >
              Cancelar
            </S.IconButton>
            <S.IconButton
              className="success"
              onClick={activateUser}
              disabled={isLoadingAPICall}
            >
              {isLoadingAPICall ? <LoadingCircle /> : 'Ativar'}
            </S.IconButton>
          </S.Buttons>
        </ModalResponsive>

        <ModalResponsive
          title={'Cadastro de cliente final'}
          isOpenModal={openRegisterModal}
          handleOpenModal={setOpenRegisterModal}
        >
          <RegistrationClientForm onCancel={handleModalClose} />
        </ModalResponsive>
      </>

      <ToastContainer
        closeButton={false}
        transition={Slide}
        position="bottom-center"
        autoClose={2000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </Container>
  );
};
